import type { ChangeEvent, FormEvent, RefObject } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { quoteFormCopy, type QuoteFieldCopy, type QuoteFormCopy } from "../content/quoteFormCopy";
import { subscriptionAgentCreditContent } from "../content/serviceCatalog";
import type { Locale } from "../content/types";
import { PublicApiError, storeCaseViewerToken, submitPublicIntake } from "../lib/apiClient";
import {
  getDefaultIntakeFormValues,
  type BankSource,
  type DeliveryPreference,
  type DynamicBooksEstimate,
  type IntakeFormField,
  type IntakeFormValues,
  type PublicIntakeResponseData,
  type ReportingNeed,
  type ValidationIssue,
  validateIntakeForm
} from "../lib/validation";

type QuotePanelState = "idle" | "validating" | "submitting" | "estimate_ready" | "error";

type QuotePanelResult =
  | {
      kind: "api";
      data: PublicIntakeResponseData;
    };

type QuoteAgentPanelProps = {
  locale: Locale;
};

export function QuoteAgentPanel({ locale }: QuoteAgentPanelProps) {
  const copy = quoteFormCopy[locale];
  const [formValues, setFormValues] = useState<IntakeFormValues>(() => getDefaultIntakeFormValues());
  const [state, setState] = useState<QuotePanelState>("idle");
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [result, setResult] = useState<QuotePanelResult | null>(null);
  const [expanded, setExpanded] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const formRegionRef = useRef<HTMLDivElement | null>(null);
  const resultRef = useRef<HTMLElement | null>(null);
  const busy = state === "validating" || state === "submitting";

  useEffect(() => {
    if (result) {
      window.setTimeout(() => resultRef.current?.focus(), 0);
    }
  }, [result]);

  const issueTextByField = useMemo(() => {
    const map = new Map<IntakeFormField, string>();
    for (const issue of issues) {
      if (!map.has(issue.field)) {
        map.set(issue.field, copy.validation[issue.code]);
      }
    }
    return map;
  }, [copy.validation, issues]);

  function updateField<Field extends keyof IntakeFormValues>(field: Field, value: IntakeFormValues[Field]) {
    setFormValues((current) => ({
      ...current,
      [field]: value
    }));
  }

  function clearResult() {
    setIssues([]);
    setApiMessage(null);
    setResult(null);
    setState("idle");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) {
      return;
    }

    setState("validating");
    setApiMessage(null);

    const validation = validateIntakeForm(formValues, {
      locale,
      pagePath: getCurrentPagePath()
    });

    if (!validation.ok) {
      setIssues(validation.issues);
      setResult(null);
      setState("error");
      setExpanded(true);
      queueSummaryFocus();
      return;
    }

    setIssues([]);
    setState("submitting");

    try {
      const data = await submitPublicIntake(validation.request);
      persistCaseSession(data);
      setResult({ kind: "api", data });
      setState("estimate_ready");
    } catch (error) {
      if (error instanceof PublicApiError && (error.code === "validation_error" || error.code === "missing_consent")) {
        setIssues(mapApiIssues(error.issues));
        setApiMessage(copy.errorSummary.validationMessage);
        setResult(null);
        setState("error");
        setExpanded(true);
        queueSummaryFocus();
        return;
      }

      setApiMessage(copy.errorSummary.retryMessage);
      setResult(null);
      setState("error");
      setExpanded(true);
      queueSummaryFocus();
    }
  }

  function expandForm() {
    setExpanded(true);
    window.setTimeout(() => formRegionRef.current?.focus(), 0);
  }

  function queueSummaryFocus() {
    window.setTimeout(() => {
      summaryRef.current?.focus();
    }, 0);
  }

  return (
    <aside className="db-first-viewport__quote db-panel db-panel--quote quote-agent-panel" aria-labelledby="quote-agent-title">
      <div className="quote-agent-panel__heading">
        <p className="section-eyebrow">{copy.heading.eyebrow}</p>
        <h2 id="quote-agent-title">{copy.heading.title}</h2>
        <p>{copy.heading.summary}</p>
        {copy.heading.recommendations && (
          <div className="quote-agent-panel__recommendations">
            <p className="quote-agent-panel__recommendations-intro">{copy.heading.recommendations.intro}</p>
            <ul>
              {copy.heading.recommendations.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="quote-agent-panel__recommendations-summary">{copy.heading.recommendations.summary}</p>
          </div>
        )}
        {state !== "idle" && (
          <p className="quote-agent-panel__status" role="status" aria-live="polite">
            {copy.status[state]}
          </p>
        )}
      </div>

      <div className="quote-agent-panel__preview quote-agent-panel__preview--actions-only">
        <button
          className="db-button db-button--primary"
          type="button"
          aria-controls="quote-agent-form-region"
          aria-expanded={expanded}
          onClick={() => {
            if (expanded) {
              setExpanded(false);
              return;
            }
            expandForm();
          }}
        >
          {expanded ? copy.heading.collapseLabel : copy.heading.expandLabel}
        </button>
      </div>

      {(issues.length > 0 || apiMessage) && (
        <div className="quote-agent-panel__alert" role="alert" tabIndex={-1} ref={summaryRef}>
          <h3>{issues.length > 0 ? copy.errorSummary.title : copy.errorSummary.apiTitle}</h3>
          {apiMessage && <p>{apiMessage}</p>}
          {issues.length > 0 && (
            <ul>
              {issues.map((issue) => (
                <li key={`${issue.field}-${issue.code}`}>
                  <a href={`#${getFieldDomId(issue.field)}`}>{copy.validation[issue.code]}</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div
        id="quote-agent-form-region"
        className="quote-agent-panel__form-region"
        hidden={!expanded}
        tabIndex={-1}
        ref={formRegionRef}
      >
      <form className="quote-agent-panel__form" onSubmit={handleSubmit} noValidate>
        <fieldset className="quote-agent-panel__fieldset" disabled={busy}>
          <legend>{copy.groups.company}</legend>
          <TextField
            field="companyName"
            copy={copy.fields.companyName}
            value={formValues.companyName}
            error={issueTextByField.get("companyName")}
            required
            onChange={(value) => updateField("companyName", value)}
          />
          <TextField
            field="contactName"
            copy={copy.fields.contactName}
            value={formValues.contactName}
            error={issueTextByField.get("contactName")}
            required
            onChange={(value) => updateField("contactName", value)}
          />
          <TextField
            field="contactEmail"
            copy={copy.fields.contactEmail}
            value={formValues.contactEmail}
            error={issueTextByField.get("contactEmail")}
            required
            type="email"
            onChange={(value) => updateField("contactEmail", value)}
          />
          <TextField
            field="contactPhone"
            copy={copy.fields.contactPhone}
            value={formValues.contactPhone}
            error={issueTextByField.get("contactPhone")}
            type="tel"
            onChange={(value) => updateField("contactPhone", value)}
          />
          <div className="quote-agent-panel__split">
            <TextField
              field="registrationNumber"
              copy={copy.fields.registrationNumber}
              value={formValues.registrationNumber}
              error={issueTextByField.get("registrationNumber")}
              required
              onChange={(value) => updateField("registrationNumber", value)}
            />
            <TextField
              field="vatNumber"
              copy={copy.fields.vatNumber}
              value={formValues.vatNumber}
              error={issueTextByField.get("vatNumber")}
              onChange={(value) => updateField("vatNumber", value)}
            />
          </div>
          <SelectField
            field="legalForm"
            copy={copy.fields.legalForm}
            value={formValues.legalForm}
            options={copy.options.legalForms}
            error={issueTextByField.get("legalForm")}
            required
            onChange={(value) => updateField("legalForm", value)}
          />
          <TextField
            field="businessDescription"
            copy={copy.fields.businessDescription}
            value={formValues.businessDescription}
            error={issueTextByField.get("businessDescription")}
            required
            multiline
            onChange={(value) => updateField("businessDescription", value)}
          />
        </fieldset>

        <fieldset className="quote-agent-panel__fieldset" disabled={busy}>
          <legend>{copy.groups.volume}</legend>
          <div className="quote-agent-panel__split">
            <TextField
              field="monthlyTransactionCount"
              copy={copy.fields.monthlyTransactionCount}
              value={formValues.monthlyTransactionCount}
              error={issueTextByField.get("monthlyTransactionCount")}
              required
              inputMode="numeric"
              onChange={(value) => updateField("monthlyTransactionCount", value)}
            />
            <TextField
              field="bankAccountCount"
              copy={copy.fields.bankAccountCount}
              value={formValues.bankAccountCount}
              error={issueTextByField.get("bankAccountCount")}
              inputMode="numeric"
              onChange={(value) => updateField("bankAccountCount", value)}
            />
          </div>
          <div className="quote-agent-panel__split">
            <TextField
              field="monthlySalesInvoiceCount"
              copy={copy.fields.monthlySalesInvoiceCount}
              value={formValues.monthlySalesInvoiceCount}
              error={issueTextByField.get("monthlySalesInvoiceCount")}
              inputMode="numeric"
              onChange={(value) => updateField("monthlySalesInvoiceCount", value)}
            />
            <TextField
              field="monthlySupplierInvoiceCount"
              copy={copy.fields.monthlySupplierInvoiceCount}
              value={formValues.monthlySupplierInvoiceCount}
              error={issueTextByField.get("monthlySupplierInvoiceCount")}
              inputMode="numeric"
              onChange={(value) => updateField("monthlySupplierInvoiceCount", value)}
            />
          </div>
          <div className="quote-agent-panel__split">
            <TextField
              field="monthlyReceiptCount"
              copy={copy.fields.monthlyReceiptCount}
              value={formValues.monthlyReceiptCount}
              error={issueTextByField.get("monthlyReceiptCount")}
              inputMode="numeric"
              onChange={(value) => updateField("monthlyReceiptCount", value)}
            />
            <TextField
              field="payrollEmployeeCount"
              copy={copy.fields.payrollEmployeeCount}
              value={formValues.payrollEmployeeCount}
              error={issueTextByField.get("payrollEmployeeCount")}
              inputMode="numeric"
              onChange={(value) => updateField("payrollEmployeeCount", value)}
            />
          </div>
          <CheckboxGroup
            field="bankSources"
            copy={copy.fields.bankSources}
            options={copy.options.bankSources}
            values={formValues.bankSources}
            error={issueTextByField.get("bankSources")}
            onChange={(value, checked) => updateField("bankSources", toggleValue(formValues.bankSources, value, checked))}
          />
          <SelectField
            field="vatPeriod"
            copy={copy.fields.vatPeriod}
            value={formValues.vatPeriod}
            options={copy.options.vatPeriods}
            error={issueTextByField.get("vatPeriod")}
            required
            onChange={(value) => updateField("vatPeriod", value)}
          />
        </fieldset>

        <fieldset className="quote-agent-panel__fieldset" disabled={busy}>
          <legend>{copy.groups.scope}</legend>
          <div className="quote-agent-panel__split">
            <TextField
              field="fixedAssetCount"
              copy={copy.fields.fixedAssetCount}
              value={formValues.fixedAssetCount}
              error={issueTextByField.get("fixedAssetCount")}
              inputMode="numeric"
              onChange={(value) => updateField("fixedAssetCount", value)}
            />
            <div className="quote-agent-panel__flag-grid">
              {copy.options.serviceFlags.map((flag) => (
                <CheckRow
                  key={flag.field}
                  id={getFieldDomId(flag.field)}
                  label={flag.label}
                  help={flag.help}
                  checked={Boolean(formValues[flag.field])}
                  onChange={(checked) => updateField(flag.field, checked)}
                />
              ))}
            </div>
          </div>
          <CheckboxGroup
            field="reportingNeeds"
            copy={copy.fields.reportingNeeds}
            options={copy.options.reportingNeeds}
            values={formValues.reportingNeeds}
            error={issueTextByField.get("reportingNeeds")}
            onChange={(value, checked) => updateField("reportingNeeds", toggleValue(formValues.reportingNeeds, value, checked))}
          />
        </fieldset>

        <fieldset className="quote-agent-panel__fieldset" disabled={busy}>
          <legend>{copy.groups.delivery}</legend>
          <SelectField
            field="deliveryPreference"
            copy={copy.fields.deliveryPreference}
            value={formValues.deliveryPreference}
            options={copy.options.deliveryPreferences}
            error={issueTextByField.get("deliveryPreference")}
            required
            onChange={(value) => updateField("deliveryPreference", value)}
          />
          <CheckRow
            id={getFieldDomId("consent")}
            label={copy.fields.consent.label}
            help={copy.fields.consent.help}
            checked={formValues.privacyAcknowledged && formValues.termsAccepted}
            error={issueTextByField.get("consent")}
            onChange={(checked) => {
              updateField("privacyAcknowledged", checked);
              updateField("termsAccepted", checked);
            }}
          />
          <CheckRow
            id="quote-marketingOptIn"
            label={copy.fields.marketingOptIn.label}
            help={copy.fields.marketingOptIn.help}
            checked={formValues.marketingOptIn}
            onChange={(checked) => updateField("marketingOptIn", checked)}
          />
        </fieldset>

        <div className="quote-agent-panel__actions">
          <button className="db-button db-button--primary" type="submit" disabled={busy}>
            {busy ? copy.actions.submitting : copy.actions.submit}
          </button>
          {result && (
            <button className="db-button db-button--secondary" type="button" onClick={clearResult}>
              {copy.actions.reset}
            </button>
          )}
        </div>
      </form>
      </div>

      {result && (
        <QuoteResultShell
          result={result}
          copy={copy}
          locale={locale}
          resultRef={resultRef}
          onBackToForm={expandForm}
        />
      )}
    </aside>
  );
}

type TextFieldProps = {
  field: IntakeFormField;
  copy: QuoteFieldCopy;
  value: string;
  error?: string;
  required?: boolean;
  type?: "email" | "tel" | "text";
  inputMode?: "numeric";
  multiline?: boolean;
  onChange: (value: string) => void;
};

function TextField({ field, copy, value, error, required = false, type = "text", inputMode, multiline = false, onChange }: TextFieldProps) {
  const id = getFieldDomId(field);
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <div className="quote-agent-panel__field">
      <label htmlFor={id}>{copy.label}</label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          placeholder={copy.placeholder}
          aria-required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy(helpId, error ? errorId : null)}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          value={value}
          type={type}
          inputMode={inputMode}
          placeholder={copy.placeholder}
          aria-required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy(helpId, error ? errorId : null)}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      <p id={helpId} className="quote-agent-panel__help">
        {copy.help}
      </p>
      {error && (
        <p id={errorId} className="quote-agent-panel__error">
          {error}
        </p>
      )}
    </div>
  );
}

type SelectFieldProps<T extends string> = {
  field: IntakeFormField;
  copy: QuoteFieldCopy;
  value: T;
  options: Array<{
    value: T;
    label: string;
  }>;
  error?: string;
  required?: boolean;
  onChange: (value: T) => void;
};

function SelectField<T extends string>({ field, copy, value, options, error, required = false, onChange }: SelectFieldProps<T>) {
  const id = getFieldDomId(field);
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <div className="quote-agent-panel__field">
      <label htmlFor={id}>{copy.label}</label>
      <select
        id={id}
        value={value}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(helpId, error ? errorId : null)}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p id={helpId} className="quote-agent-panel__help">
        {copy.help}
      </p>
      {error && (
        <p id={errorId} className="quote-agent-panel__error">
          {error}
        </p>
      )}
    </div>
  );
}

type CheckboxGroupProps<T extends BankSource | ReportingNeed> = {
  field: "bankSources" | "reportingNeeds";
  copy: QuoteFieldCopy;
  options: Array<{
    value: T;
    label: string;
  }>;
  values: T[];
  error?: string;
  onChange: (value: T, checked: boolean) => void;
};

function CheckboxGroup<T extends BankSource | ReportingNeed>({ field, copy, options, values, error, onChange }: CheckboxGroupProps<T>) {
  const id = getFieldDomId(field);
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <div
      className="quote-agent-panel__field quote-agent-panel__field--wide"
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy(helpId, error ? errorId : null)}
    >
      <div id={`${id}-label`} className="quote-agent-panel__label">
        {copy.label}
      </div>
      <p id={helpId} className="quote-agent-panel__help">
        {copy.help}
      </p>
      <div className="quote-agent-panel__checkbox-grid">
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          return (
            <label key={option.value} className="quote-agent-panel__check" htmlFor={optionId}>
              <input
                id={optionId}
                type="checkbox"
                checked={values.includes(option.value)}
                onChange={(event) => onChange(option.value, event.target.checked)}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="quote-agent-panel__error">
          {error}
        </p>
      )}
    </div>
  );
}

type CheckRowProps = {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  error?: string;
  onChange: (checked: boolean) => void;
};

function CheckRow({ id, label, help, checked, error, onChange }: CheckRowProps) {
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <label className="quote-agent-panel__check quote-agent-panel__check--stack" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(helpId, error ? errorId : null)}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        <small id={helpId}>{help}</small>
        {error && (
          <small id={errorId} className="quote-agent-panel__error">
            {error}
          </small>
        )}
      </span>
    </label>
  );
}

type QuoteResultShellProps = {
  result: QuotePanelResult;
  copy: QuoteFormCopy;
  locale: Locale;
  resultRef: RefObject<HTMLElement | null>;
  onBackToForm: () => void;
};

function QuoteResultShell({ result, copy, locale, resultRef, onBackToForm }: QuoteResultShellProps) {
  const estimate = result.data.estimate;
  const status = (copy.result.caseStatusLabels as Record<string, string>)[result.data.status] ?? copy.result.caseStatusLabels.estimate_ready;
  const bidStatus = (copy.result.bidStatusLabels as Record<string, string>)[result.data.bid_delivery.status] ?? copy.result.bidStatusLabels.queued;
  const bidMessage = (copy.result.bidStatusMessages as Record<string, string>)[result.data.bid_delivery.status] ?? copy.result.bidStatusMessages.queued;
  const disclaimer = estimate.disclaimer[locale] ?? copy.result.disclaimerFallback;
  const billingHandoffNote = subscriptionAgentCreditContent[locale].cards.find((card) => card.key === "dynamicmail_billing_handoff")?.body;

  return (
    <section className="quote-agent-result" aria-labelledby="quote-agent-result-title" tabIndex={-1} ref={resultRef}>
      <div className="quote-agent-result__header">
        <h3 id="quote-agent-result-title">{copy.result.title}</h3>
        <p>{copy.result.apiNotice}</p>
      </div>

      <dl className="quote-agent-result__metrics">
        <div>
          <dt>{copy.result.statusLabel}</dt>
          <dd>{status}</dd>
        </div>
        <div>
          <dt>{copy.result.rangeLabel}</dt>
          <dd>{estimate ? formatPriceBand(estimate, locale, copy) : copy.result.pendingRange}</dd>
        </div>
        <div>
          <dt>{copy.result.confidenceLabel}</dt>
          <dd>{estimate?.confidence ?? copy.result.pendingConfidence}</dd>
        </div>
        <div>
          <dt>{copy.result.caseLabel}</dt>
          <dd>
            <a href={result.data.case_path} aria-label={`${copy.result.caseOpenLabel}: ${result.data.case_public_id}`}>
              {result.data.case_public_id}
            </a>
          </dd>
        </div>
      </dl>

      <div className="quote-agent-result__grid">
        <ResultBlock title={copy.result.packageLabel} lines={[estimate.service_package]} />
        <ResultBlock title={copy.result.creditsLabel} lines={formatCredits(estimate, locale, copy, billingHandoffNote)} />
        <ResultBlock title={copy.result.tierLabel} lines={formatTier(estimate, copy, billingHandoffNote)} />
        <ResultBlock title={copy.result.bidLabel} lines={[bidStatus, bidMessage]} />
        <ResultBlock title={copy.result.assumptionsLabel} lines={estimate.assumptions} emptyText={copy.result.emptyList} />
        <ResultBlock title={copy.result.includedLabel} lines={estimate.included_workstreams} emptyText={copy.result.emptyList} />
        <ResultBlock title={copy.result.notIncludedLabel} lines={estimate.not_included} emptyText={copy.result.emptyList} />
        <ResultBlock title={copy.result.nextStepsLabel} lines={estimate.next_steps} emptyText={copy.result.emptyList} />
      </div>

      <p className="quote-agent-result__disclaimer">{disclaimer}</p>
      <button className="db-button db-button--secondary" type="button" onClick={onBackToForm}>
        {copy.heading.resultBackLabel}
      </button>
    </section>
  );
}

function ResultBlock({ title, lines, emptyText }: { title: string; lines: string[]; emptyText?: string }) {
  const safeLines = lines.length > 0 ? lines : emptyText ? [emptyText] : [];
  return (
    <section className="quote-agent-result__block">
      <h4>{title}</h4>
      <ul>
        {safeLines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </section>
  );
}

function formatPriceBand(estimate: DynamicBooksEstimate, locale: Locale, copy: QuoteFormCopy): string {
  const formatter = new Intl.NumberFormat(locale === "sv" ? "sv-SE" : "en-SE", {
    style: "currency",
    currency: "SEK",
    maximumFractionDigits: 0
  });
  const monthly = `${formatter.format(estimate.price_band.min_monthly_sek)}-${formatter.format(estimate.price_band.max_monthly_sek)}`;
  const setup = `${formatter.format(estimate.price_band.setup_min_sek)}-${formatter.format(estimate.price_band.setup_max_sek)}`;
  return `${monthly} ${copy.result.monthlyUnit}, ${copy.result.setupLabel} ${setup}`;
}

function formatCredits(estimate: DynamicBooksEstimate | null, locale: Locale, copy: QuoteFormCopy, billingHandoffNote?: string): string[] {
  if (!estimate) {
    return [copy.result.pendingCredits, ...(billingHandoffNote ? [billingHandoffNote] : [])];
  }

  const pack = estimate.agent_credits.suggested_initial_credit_pack;
  const initialPack = typeof pack === "number" ? formatAgentCreditValue(pack, locale) : pack;
  return [
    `${formatAgentCreditValue(estimate.agent_credits.expected_monthly_credits, locale)} ${copy.result.creditMonthUnit}, ${copy.result.initialPackLabel} ${initialPack}`,
    ...estimate.agent_credits.credit_assumptions,
    ...(billingHandoffNote ? [billingHandoffNote] : [])
  ];
}

function formatAgentCreditValue(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "sv" ? "sv-SE" : "en-US", {
    maximumFractionDigits: 0
  })
    .format(value)
    .replace(/\u00a0/g, " ");
}

function formatTier(estimate: DynamicBooksEstimate | null, copy: QuoteFormCopy, billingHandoffNote?: string): string[] {
  return [estimate?.suggested_subscription_tier ?? copy.result.pendingTier, ...(billingHandoffNote ? [billingHandoffNote] : [])];
}

function mapApiIssues(issues: Array<{ path: string; code: string }>): ValidationIssue[] {
  const mapped = issues
    .map((issue) => ({
      field: mapApiPathToField(issue.path),
      code: issue.code === "missing_consent" ? "missing_consent" : "required"
    }))
    .filter((issue): issue is ValidationIssue => issue.field !== null);

  return mapped.length > 0 ? mapped : [{ field: "companyName", code: "required" }];
}

function mapApiPathToField(path: string): IntakeFormField | null {
  if (path.includes("company_name")) return "companyName";
  if (path.includes("contact_name")) return "contactName";
  if (path.includes("business_email")) return "contactEmail";
  if (path.includes("contact_phone")) return "contactPhone";
  if (path.includes("registration_number")) return "registrationNumber";
  if (path.includes("vat_number")) return "vatNumber";
  if (path.includes("legal_form")) return "legalForm";
  if (path.includes("business_description")) return "businessDescription";
  if (path.includes("monthly_transaction_count")) return "monthlyTransactionCount";
  if (path.includes("monthly_sales_invoice_count")) return "monthlySalesInvoiceCount";
  if (path.includes("monthly_supplier_invoice_count")) return "monthlySupplierInvoiceCount";
  if (path.includes("monthly_receipt_count")) return "monthlyReceiptCount";
  if (path.includes("payroll_employee_count")) return "payrollEmployeeCount";
  if (path.includes("bank_account_count")) return "bankAccountCount";
  if (path.includes("bank_sources")) return "bankSources";
  if (path.includes("vat_period")) return "vatPeriod";
  if (path.includes("fixed_asset_count")) return "fixedAssetCount";
  if (path.includes("reporting_needs")) return "reportingNeeds";
  if (path.includes("delivery_preference")) return "deliveryPreference";
  if (path.includes("consent")) return "consent";
  return null;
}

function getCurrentPagePath(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.location.pathname || "/";
}

function persistCaseSession(data: PublicIntakeResponseData) {
  storeCaseViewerToken(data.case_public_id, data.viewer_token);
}

function toggleValue<T extends string>(values: T[], value: T, checked: boolean): T[] {
  if (checked) {
    return values.includes(value) ? values : [...values, value];
  }
  return values.filter((item) => item !== value);
}

function getFieldDomId(field: IntakeFormField): string {
  return `quote-${field}`;
}

function describedBy(...ids: Array<string | null>): string | undefined {
  const safeIds = ids.filter((id): id is string => Boolean(id));
  return safeIds.length > 0 ? safeIds.join(" ") : undefined;
}
